import { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAdminStatus } from "@/hooks/useAdminStatus";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { ClipboardCheck, Calendar, Clock, User, Package, CheckCircle, XCircle, Edit2, QrCode, ZoomIn, Camera, Upload, Loader2, Truck, RotateCcw, ChevronDown, ChevronRight, MapPin, Filter, ArrowUpDown, Store, Eye, Undo2, Crop, Printer } from "lucide-react";
import { formatRetailerName } from "@/lib/formatRetailer";
import { notifyPackageDenied, notifyPickupDenied } from "@/lib/notifications";
import { AdminContentHeader } from "@/components/admin/AdminContentHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EditPackageDialog } from "@/components/admin/EditPackageDialog";
import { StorageImage } from "@/components/StorageImage";

import { ReturnCompletionDialog } from "@/components/admin/ReturnCompletionDialog";
import { ProposePickupDialog } from "@/components/admin/ProposePickupDialog";
import { PackageDetailDrawer } from "@/components/admin/PackageDetailDrawer";
import { QrCropDialog } from "@/components/admin/QrCropDialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface PackageData {
  id: string;
  retailer: string;
  size: string;
  status: string;
  approval_status: string;
  package_photo_url: string | null;
  qr_code_url: string | null;
  proof_of_delivery_url: string | null;
  proof_of_pickup_url: string | null;
  return_receipt_url: string | null;
  confirmation_code: string | null;
  returned_at: string | null;
  created_at: string;
  notes: string | null;
  cropped_qr_url: string | null;
  pickupSchedule?: {
    id: string;
    pickup_date: string;
    pickup_time_slot: string;
  };
}

interface AssignedUser {
  id: string;
  fullName: string;
  email: string;
  phone?: string;
  address?: string;
  assignedOwnerId?: string;
  assignedOwnerName?: string;
  pickupId?: string;
  pickupDate?: string;
  pickupTime?: string;
  pickupStatus?: string; // from user_pickups: awaiting_confirmation, confirmed, etc.
  pickupAddress?: string;
  pickupConfirmationPhotoUrl?: string | null;
  packages: PackageData[];
}

interface LogisticsManager {
  id: string;
  name: string;
  email: string;
  role: "admin" | "manager";
}

export const AdminTasks = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "pending";
  const setActiveTab = useCallback((tab: string) => {
    setSearchParams({ tab }, { replace: true });
  }, [setSearchParams]);
  const { user } = useAuth();
  const { isAdmin } = useAdminStatus();
  const [assignedUsers, setAssignedUsers] = useState<AssignedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [logisticsManagers, setLogisticsManagers] = useState<LogisticsManager[]>([]);
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [ownerFilter, setOwnerFilter] = useState<string>("all");
  const [_pendingSortBy, _setPendingSortBy] = useState<string>("pickup-date");
  const [_scheduledSortBy, _setScheduledSortBy] = useState<string>("pickup-date");
  const [deliveredSortBy, setDeliveredSortBy] = useState<string>("return-date");

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<{
    id: string;
    managerId?: string;
    pickupDate?: Date;
    pickupTimeSlot?: string;
  } | null>(null);
  const [editLoading, setEditLoading] = useState(false);

  // Approval dialog state
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvingPackageId, setApprovingPackageId] = useState<string | null>(null);
  const [approvalAction, setApprovalAction] = useState<"approve" | "deny">("approve");
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [denialReason, setDenialReason] = useState("");

  // Image preview state
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<{ bucket: string; path: string; title: string } | null>(null);

  // Mark as Picked Up dialog state
  const [pickupDialogOpen, setPickupDialogOpen] = useState(false);
  const [pickingUpPackageId, setPickingUpPackageId] = useState<string | null>(null);
  const [proofOfPickupFile, setProofOfPickupFile] = useState<File | null>(null);
  const [proofOfPickupPreview, setProofOfPickupPreview] = useState<string | null>(null);
  const [pickupLoading, setPickupLoading] = useState(false);

  // Return completion dialog state
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returningPackageId, setReturningPackageId] = useState<string | null>(null);
  const [returningPackageName, setReturningPackageName] = useState("");

  // Per-user pickup confirmation photo upload state
  const [pickupConfirmDialogOpen, setPickupConfirmDialogOpen] = useState(false);
  const [confirmingUserId, setConfirmingUserId] = useState<string | null>(null);
  const [confirmationPhotoFile, setConfirmationPhotoFile] = useState<File | null>(null);
  const [confirmationPhotoPreview, setConfirmationPhotoPreview] = useState<string | null>(null);
  const [confirmationLoading, setConfirmationLoading] = useState(false);

  // Counter-proposal (suggest new time) state
  const [suggestTimeDialogOpen, setSuggestTimeDialogOpen] = useState(false);
  const [suggestTimeUserId, setSuggestTimeUserId] = useState<string | null>(null);
  const [suggestTimeLoading, setSuggestTimeLoading] = useState(false);

  // Package detail drawer state
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [detailPackageData, setDetailPackageData] = useState<any>(null);

  // Undo lifecycle state
  const [undoDialogOpen, setUndoDialogOpen] = useState(false);
  const [undoPackageId, setUndoPackageId] = useState<string | null>(null);
  const [undoReason, setUndoReason] = useState("");
  const [undoLoading, setUndoLoading] = useState(false);
  const [undoAction, setUndoAction] = useState<{
    label: string;
    targetApprovalStatus?: string;
    targetStatus?: string;
    pickupId?: string;
    resetPickup?: boolean;
    isUserLevel?: boolean;
    userPackageIds?: string[];
  } | null>(null);

  // QR Crop dialog state
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [cropPackage, setCropPackage] = useState<{
    id: string;
    qrCodeUrl: string | null;
    retailer: string;
    pickupDate: string | null;
    sequenceNumber: number;
  } | null>(null);

  const handleImagePreview = (bucket: string, path: string, title: string) => {
    setPreviewImage({ bucket, path, title });
    setImagePreviewOpen(true);
  };

  const handleViewPackageDetails = (pkg: PackageData, userInfo: AssignedUser) => {
    setDetailPackageData({
      id: pkg.id,
      retailer: pkg.retailer,
      size: pkg.size,
      status: pkg.status === "picked_up" ? "picked-up" : pkg.approval_status === "pending" ? "pending-review" : pkg.approval_status === "approved" ? "confirmed" : pkg.status,
      thumbnailUrl: pkg.package_photo_url || undefined,
      qrCodeUrl: pkg.qr_code_url || undefined,
      user: {
        fullName: userInfo.fullName,
        email: userInfo.email,
        phone: userInfo.phone,
        address: userInfo.pickupAddress || userInfo.address,
      },
      manager: userInfo.assignedOwnerName ? { name: userInfo.assignedOwnerName } : undefined,
      proposedPickup: userInfo.pickupDate ? {
        date: userInfo.pickupDate,
        timeWindow: userInfo.pickupTime || "",
      } : undefined,
      timeline: [
        { id: "1", status: "Package Submitted", timestamp: pkg.created_at ? format(new Date(pkg.created_at), "MMM d, yyyy 'at' h:mm a") : "Unknown", description: "" },
      ],
      submittedAt: pkg.created_at ? format(new Date(pkg.created_at), "MMM d, yyyy 'at' h:mm a") : undefined,
      notes: pkg.notes || undefined,
    });
    setDetailDrawerOpen(true);
  };

  const openUndoDialog = (packageId: string, action: typeof undoAction) => {
    setUndoPackageId(packageId);
    setUndoAction(action);
    setUndoReason("");
    setUndoDialogOpen(true);
  };

  const handleUndoConfirm = async () => {
    if (!undoPackageId || !undoAction || !undoReason.trim()) return;

    setUndoLoading(true);
    try {
      const undoNote = `[UNDO - ${undoAction.label}] ${undoReason.trim()}`;
      const updateData: Record<string, any> = { updated_at: new Date().toISOString() };

      if (undoAction.targetApprovalStatus) {
        updateData.approval_status = undoAction.targetApprovalStatus;
      }
      if (undoAction.targetStatus) {
        updateData.status = undoAction.targetStatus;
      }
      if (undoAction.targetApprovalStatus === "pending") {
        updateData.rejection_reason = null;
      }
      if (undoAction.targetStatus === "picked_up") {
        updateData.returned_at = null;
        updateData.return_receipt_url = null;
      }

      // User-level undo: update all packages at once
      if (undoAction.isUserLevel && undoAction.userPackageIds?.length) {
        // Fetch existing notes for each package to prepend undo reason
        const { data: existingPkgs } = await (supabase as any)
          .from("packages")
          .select("id, notes")
          .in("id", undoAction.userPackageIds);

        for (const pkg of (existingPkgs || [])) {
          const newNotes = pkg.notes ? `${undoNote}\n---\n${pkg.notes}` : undoNote;
          await (supabase as any)
            .from("packages")
            .update({ ...updateData, notes: newNotes })
            .eq("id", pkg.id);
        }
      } else {
        // Single package: fetch existing notes and prepend
        const { data: existingPkg } = await (supabase as any)
          .from("packages")
          .select("notes")
          .eq("id", undoPackageId)
          .maybeSingle();

        const newNotes = existingPkg?.notes ? `${undoNote}\n---\n${existingPkg.notes}` : undoNote;
        const { error: pkgError } = await (supabase as any)
          .from("packages")
          .update({ ...updateData, notes: newNotes })
          .eq("id", undoPackageId);

        if (pkgError) throw pkgError;
      }

      // If we need to reset the pickup status (e.g., undo picked up → back to confirmed)
      if (undoAction.resetPickup && undoAction.pickupId) {
        const { error: pickupError } = await (supabase as any)
          .from("user_pickups")
          .update({
            status: "confirmed",
            completed_at: null,
            pickup_confirmation_photo_url: null,
            updated_at: new Date().toISOString(),
          })
          .eq("id", undoAction.pickupId);

        if (pickupError) throw pickupError;
      }

      toast.success(`${undoAction.label} reversed successfully`);
      setUndoDialogOpen(false);
      setUndoPackageId(null);
      setUndoAction(null);
      setUndoReason("");
      await fetchAssignedUsers();
    } catch (error: any) {
      console.error("Error undoing action:", error);
      toast.error(error.message || "Failed to reverse action");
    } finally {
      setUndoLoading(false);
    }
  };

  const handleOpenPickupDialog = (packageId: string) => {
    setPickingUpPackageId(packageId);
    setProofOfPickupFile(null);
    setProofOfPickupPreview(null);
    setPickupDialogOpen(true);
  };

  const handlePickupProofFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProofOfPickupFile(file);
      const previewUrl = URL.createObjectURL(file);
      setProofOfPickupPreview(previewUrl);
    }
  };

  const handleMarkAsPickedUp = async () => {
    if (!pickingUpPackageId) return;

    setPickupLoading(true);
    try {
      let proofFileName: string | null = null;

      if (proofOfPickupFile) {
        const fileExt = proofOfPickupFile.name.split('.').pop();
        proofFileName = `${pickingUpPackageId}/${Date.now()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('pickup-proof')
          .upload(proofFileName, proofOfPickupFile);

        if (uploadError) throw uploadError;
      }

      const updateData: { status: string; proof_of_pickup_url?: string } = {
        status: 'picked_up'
      };

      if (proofFileName) {
        updateData.proof_of_pickup_url = proofFileName;
      }

      const { error: updateError } = await (supabase as any)
        .from('packages')
        .update(updateData)
        .eq('id', pickingUpPackageId);

      if (updateError) throw updateError;

      toast.success("Package marked as picked up");
      setPickupDialogOpen(false);
      setPickingUpPackageId(null);
      setProofOfPickupFile(null);
      setProofOfPickupPreview(null);
      await fetchAssignedUsers();
    } catch (error: any) {
      console.error("Error marking as picked up:", error);
      toast.error(error.message || "Failed to mark as picked up");
    } finally {
      setPickupLoading(false);
    }
  };

  const fetchLogisticsManagers = async () => {
    try {
      const { data: roleData, error: roleError } = await supabase
        .from("user_roles")
        .select("user_id, role")
        .in("role", ["admin", "manager"]);

      if (roleError) throw roleError;

      if (!roleData || roleData.length === 0) {
        setLogisticsManagers([]);
        return;
      }

      const userRoleMap = new Map<string, "admin" | "manager">();
      roleData.forEach((r) => {
        if (!userRoleMap.has(r.user_id) || r.role === "admin") {
          userRoleMap.set(r.user_id, r.role as "admin" | "manager");
        }
      });

      const userIds = Array.from(userRoleMap.keys());

      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("id, email, first_name, last_name")
        .in("id", userIds);

      if (profileError) throw profileError;

      const managers: LogisticsManager[] = (profileData || []).map((profile: any) => {
        const firstName = profile.first_name || "";
        const lastName = profile.last_name || "";
        const fullName = firstName && lastName
          ? `${firstName} ${lastName}`
          : profile.email?.split("@")[0] || "Unknown";

        return {
          id: profile.id,
          name: fullName,
          email: profile.email || "",
          role: userRoleMap.get(profile.id) || "manager",
        };
      });

      setLogisticsManagers(managers);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error fetching logistics managers:", error);
      }
    }
  };

  const fetchAssignedUsers = async () => {
    if (!user?.id) return;

    try {
      // For non-admin roles, first get the list of user IDs assigned to this manager
      let assignedUserIds: string[] | null = null;
      if (!isAdmin) {
        const { data: assignedProfiles, error: assignedError } = await supabase
          .from("profiles")
          .select("id")
          .eq("manager_id", user.id);
        if (assignedError) throw assignedError;
        assignedUserIds = (assignedProfiles || []).map((p: any) => p.id);
        // If no users assigned, return empty
        if (assignedUserIds.length === 0) {
          setAssignedUsers([]);
          setLoading(false);
          return;
        }
      }

      // Fetch packages with profiles
      let packagesQuery = (supabase as any)
        .from("packages")
        .select(`
          *,
          pickup_schedules (*),
          profiles!packages_user_id_fkey (id, email, phone_number, street, city, state, zip, first_name, last_name, manager_id)
        `)
        .order("created_at", { ascending: false });

      if (assignedUserIds) {
        packagesQuery = packagesQuery.in("user_id", assignedUserIds);
      }

      // Fetch user pickups
      let pickupsQuery = (supabase as any)
        .from("user_pickups")
        .select("*")
        .order("proposed_date", { ascending: true });

      if (assignedUserIds) {
        pickupsQuery = pickupsQuery.in("user_id", assignedUserIds);
      }

      const [packagesResult, pickupsResult] = await Promise.all([packagesQuery, pickupsQuery]);

      if (packagesResult.error) throw packagesResult.error;

      // Build a map of user_id → most relevant pickup (prefer active over completed)
      const activeStatuses = ["awaiting_confirmation", "awaiting_admin_confirmation", "counter_proposed", "confirmed"];
      const pickupsByUser = new Map<string, any>();
      (pickupsResult.data || []).forEach((pickup: any) => {
        const existing = pickupsByUser.get(pickup.user_id);
        if (!existing) {
          pickupsByUser.set(pickup.user_id, pickup);
        } else {
          // Prefer active pickup over completed/cancelled
          const existingIsActive = activeStatuses.includes(existing.status);
          const newIsActive = activeStatuses.includes(pickup.status);
          if (newIsActive && !existingIsActive) {
            pickupsByUser.set(pickup.user_id, pickup);
          }
        }
      });

      // Group packages by user
      const userMap = new Map<string, AssignedUser>();

      (packagesResult.data || []).forEach((pkg: any) => {
        const userId = pkg.user_id;
        if (!userMap.has(userId)) {
          const firstName = pkg.profiles?.first_name || "";
          const lastName = pkg.profiles?.last_name || "";
          const fullName = firstName && lastName
            ? `${firstName} ${lastName}`
            : pkg.profiles?.email?.split("@")[0] || "Unknown";

          const address = pkg.profiles
            ? `${pkg.profiles.street || ""}, ${pkg.profiles.city || ""}, ${pkg.profiles.state || ""} ${pkg.profiles.zip || ""}`.trim().replace(/^,\s*/, "")
            : undefined;

          const userPickup = pickupsByUser.get(userId);

          userMap.set(userId, {
            id: userId,
            fullName,
            email: pkg.profiles?.email || "Unknown",
            phone: pkg.profiles?.phone_number,
            address,
            assignedOwnerId: pkg.profiles?.manager_id || undefined,
            assignedOwnerName: undefined, // resolved after fetch
            pickupId: userPickup?.id,
            pickupDate: userPickup?.proposed_date,
            pickupTime: userPickup?.proposed_time_slot,
            pickupStatus: userPickup?.status,
            pickupAddress: userPickup?.address,
            pickupConfirmationPhotoUrl: userPickup?.pickup_confirmation_photo_url || null,
            packages: [],
          });
        }

        const packageData: PackageData = {
          id: pkg.id,
          retailer: pkg.retailer,
          size: pkg.size,
          status: pkg.status,
          approval_status: pkg.approval_status,
          package_photo_url: pkg.package_photo_url,
          qr_code_url: pkg.qr_code_url,
          proof_of_delivery_url: pkg.proof_of_delivery_url,
          proof_of_pickup_url: pkg.proof_of_pickup_url,
          return_receipt_url: pkg.return_receipt_url,
          confirmation_code: pkg.confirmation_code,
          returned_at: pkg.returned_at,
          created_at: pkg.created_at,
          notes: pkg.notes,
          cropped_qr_url: pkg.cropped_qr_url || null,
          pickupSchedule: pkg.pickup_schedules?.[0] || undefined,
        };

        userMap.get(userId)!.packages.push(packageData);
      });

      // Resolve owner names inline if managers are already loaded
      const users = Array.from(userMap.values());
      if (logisticsManagers.length > 0) {
        const managerMap = new Map(logisticsManagers.map(m => [m.id, m.name]));
        users.forEach(u => {
          u.assignedOwnerName = u.assignedOwnerId ? managerMap.get(u.assignedOwnerId) || undefined : undefined;
        });
      }
      setAssignedUsers(users);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error fetching assigned users:", error);
      }
      toast.error("Failed to load your tasks");
    } finally {
      setLoading(false);
    }
  };

  // Resolve owner names when managers load after users
  useEffect(() => {
    if (logisticsManagers.length > 0 && assignedUsers.length > 0) {
      const managerMap = new Map(logisticsManagers.map(m => [m.id, m.name]));
      const needsUpdate = assignedUsers.some(u => u.assignedOwnerId && !u.assignedOwnerName);
      if (needsUpdate) {
        setAssignedUsers(prev => prev.map(u => ({
          ...u,
          assignedOwnerName: u.assignedOwnerId ? managerMap.get(u.assignedOwnerId) || undefined : undefined,
        })));
      }
    }
  }, [logisticsManagers.length]);

  useEffect(() => {
    fetchAssignedUsers();
    fetchLogisticsManagers();
  }, [user?.id, isAdmin]);

  // Filter users by owner (admin only)
  const filteredUsers = useMemo(() => {
    if (!isAdmin || ownerFilter === "all") return assignedUsers;
    if (ownerFilter === "unassigned") return assignedUsers.filter(u => !u.assignedOwnerId);
    return assignedUsers.filter(u => u.assignedOwnerId === ownerFilter);
  }, [assignedUsers, ownerFilter, isAdmin]);

  const toggleUserExpand = (userId: string) => {
    setExpandedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const findPackageUser = (packageId: string): AssignedUser | undefined => {
    return assignedUsers.find(u => u.packages.some(p => p.id === packageId));
  };

  // Pickup date/time approval
  const [pickupApprovalDialogOpen, setPickupApprovalDialogOpen] = useState(false);
  const [pickupApprovalAction, setPickupApprovalAction] = useState<"approve" | "deny">("approve");
  const [pickupApprovalUserId, setPickupApprovalUserId] = useState<string | null>(null);
  const [pickupApprovalLoading, setPickupApprovalLoading] = useState(false);
  const [pickupDenialReason, setPickupDenialReason] = useState("");

  const handlePickupApprovalAction = (userId: string, action: "approve" | "deny") => {
    setPickupApprovalUserId(userId);
    setPickupApprovalAction(action);
    setPickupDenialReason("");
    setPickupApprovalDialogOpen(true);
  };

  const confirmPickupApproval = async () => {
    if (!pickupApprovalUserId) return;
    const userInfo = assignedUsers.find(u => u.id === pickupApprovalUserId);
    if (!userInfo?.pickupId) return;

    if (pickupApprovalAction === "deny" && !pickupDenialReason.trim()) {
      toast.error("Please provide a reason for denying the pickup");
      return;
    }

    setPickupApprovalLoading(true);
    try {
      if (pickupApprovalAction === "approve") {
        const { error } = await (supabase as any)
          .from("user_pickups")
          .update({ status: "confirmed" })
          .eq("id", userInfo.pickupId);
        if (error) throw error;
        toast.success("Pickup date/time approved");
      } else {
        const { error } = await (supabase as any)
          .from("user_pickups")
          .update({
            status: "needs_user_response",
            denial_reason: pickupDenialReason.trim(),
          })
          .eq("id", userInfo.pickupId);
        if (error) throw error;

        // Notify the user to pick a new time
        await notifyPickupDenied(pickupApprovalUserId, pickupDenialReason.trim());

        toast.success("Pickup denied — user has been notified to pick a new time");
      }
      await fetchAssignedUsers();
    } catch (error) {
      console.error("Error updating pickup status:", error);
      toast.error("Failed to update pickup status");
    } finally {
      setPickupApprovalLoading(false);
      setPickupApprovalDialogOpen(false);
      setPickupApprovalUserId(null);
      setPickupDenialReason("");
    }
  };

  const handleEditPackage = (pkg: PackageData) => {
    setEditingPackage({
      id: pkg.id,
      managerId: user?.id,
      pickupDate: pkg.pickupSchedule ? new Date(pkg.pickupSchedule.pickup_date) : undefined,
      pickupTimeSlot: pkg.pickupSchedule?.pickup_time_slot || undefined,
    });
    setEditDialogOpen(true);
  };

  const handleSavePackage = async (data: {
    packageId: string;
    managerId: string | null;
    pickupDate: Date | null;
    pickupTimeSlot: string | null;
  }) => {
    setEditLoading(true);
    try {
      const { error: packageError } = await (supabase as any)
        .from("packages")
        .update({
          manager_id: data.managerId,
          status: data.pickupDate && data.pickupTimeSlot ? "scheduled" : "pending"
        })
        .eq("id", data.packageId);

      if (packageError) throw packageError;

      const ownerUser = findPackageUser(data.packageId);
      const pkg = ownerUser?.packages.find(p => p.id === data.packageId);
      const existingSchedule = pkg?.pickupSchedule;

      if (data.pickupDate && data.pickupTimeSlot) {
        const address = ownerUser?.address || "Address not available";

        if (existingSchedule) {
          const { error: scheduleError } = await (supabase as any)
            .from("pickup_schedules")
            .update({
              pickup_date: format(data.pickupDate, "yyyy-MM-dd"),
              pickup_time_slot: data.pickupTimeSlot,
            })
            .eq("id", existingSchedule.id);

          if (scheduleError) throw scheduleError;
        } else {
          const { error: scheduleError } = await (supabase as any)
            .from("pickup_schedules")
            .insert({
              package_id: data.packageId,
              user_id: ownerUser?.id,
              pickup_date: format(data.pickupDate, "yyyy-MM-dd"),
              pickup_time_slot: data.pickupTimeSlot,
              address: address,
            });

          if (scheduleError) throw scheduleError;
        }
      } else if (existingSchedule && !data.pickupDate) {
        await (supabase as any)
          .from("pickup_schedules")
          .delete()
          .eq("id", existingSchedule.id);
      }

      toast.success("Package updated successfully");
      await fetchAssignedUsers();
      setEditDialogOpen(false);
      setEditingPackage(null);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error updating package:", error);
      }
      toast.error("Failed to update package");
    } finally {
      setEditLoading(false);
    }
  };

  const handleApprovalAction = (packageId: string, action: "approve" | "deny") => {
    setApprovingPackageId(packageId);
    setApprovalAction(action);
    setDenialReason("");
    setApprovalDialogOpen(true);
  };

  const confirmApproval = async () => {
    if (!approvingPackageId) return;

    if (approvalAction === "deny" && !denialReason.trim()) {
      toast.error("Please provide a reason for denying the package");
      return;
    }

    setApprovalLoading(true);
    try {
      const updateData: { approval_status: string; rejection_reason?: string | null } = {
        approval_status: approvalAction === "approve" ? "approved" : "rejected",
      };

      if (approvalAction === "deny") {
        updateData.rejection_reason = denialReason.trim();
      } else {
        updateData.rejection_reason = null;
      }

      const { error } = await (supabase as any)
        .from("packages")
        .update(updateData)
        .eq("id", approvingPackageId);

      if (error) throw error;

      if (approvalAction === "deny") {
        const ownerUser = findPackageUser(approvingPackageId);
        const pkg = ownerUser?.packages.find(p => p.id === approvingPackageId);
        if (ownerUser && pkg) {
          await notifyPackageDenied(
            ownerUser.id,
            pkg.retailer,
            denialReason.trim()
          );
        }
      }

      toast.success(
        approvalAction === "approve"
          ? "Package approved successfully"
          : "Package rejected"
      );
      await fetchAssignedUsers();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error updating approval status:", error);
      }
      toast.error("Failed to update package status");
    } finally {
      setApprovalLoading(false);
      setApprovalDialogOpen(false);
      setApprovingPackageId(null);
      setDenialReason("");
    }
  };

  const handleOpenReturnDialog = (pkg: PackageData) => {
    setReturningPackageId(pkg.id);
    setReturningPackageName(formatRetailerName(pkg.retailer));
    setReturnDialogOpen(true);
  };

  const getStatusBadge = (pkg: PackageData) => {
    if (pkg.approval_status === "pending") {
      return <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">Pending Review</Badge>;
    }
    if (pkg.approval_status === "rejected") {
      return <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200">Rejected</Badge>;
    }
    if (pkg.status === "delivered") {
      return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">Delivered</Badge>;
    }
    if (pkg.status === "picked_up") {
      return <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200">Picked Up</Badge>;
    }
    if (pkg.status === "scheduled") {
      return <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-200">Scheduled</Badge>;
    }
    if (pkg.approval_status === "approved") {
      return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">Approved</Badge>;
    }
    return <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-200">Awaiting Schedule</Badge>;
  };

  const PackageCard = ({ pkg, userInfo, hideEditSchedule = false, showMarkReturned = false }: { pkg: PackageData; userInfo: AssignedUser; hideEditSchedule?: boolean; showMarkReturned?: boolean }) => (
    <div className="bg-card rounded-lg border shadow-sm hover:shadow-md transition-shadow p-4 sm:p-5">
      {/* Header Row */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Package Photo Thumbnail */}
          <div
            className={`w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-muted overflow-hidden border flex-shrink-0 ${pkg.package_photo_url ? 'cursor-pointer group relative' : ''}`}
            onClick={() => pkg.package_photo_url && handleImagePreview("package-photos", pkg.package_photo_url, "Package Photo")}
          >
            {pkg.package_photo_url ? (
              <>
                <StorageImage
                  bucket="package-photos"
                  storedValue={pkg.package_photo_url}
                  alt="Package"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <ZoomIn className="h-4 w-4 text-white" />
                </div>
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package className="h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground" />
              </div>
            )}
          </div>

          {/* QR Code Thumbnail */}
          <div
            className={`w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-muted overflow-hidden border flex-shrink-0 ${pkg.qr_code_url ? 'cursor-pointer group relative' : ''}`}
            onClick={() => pkg.qr_code_url && handleImagePreview("qr-codes", pkg.qr_code_url, "QR Code")}
          >
            {pkg.qr_code_url ? (
              <>
                <StorageImage
                  bucket="qr-codes"
                  storedValue={pkg.qr_code_url}
                  alt="QR Code"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <ZoomIn className="h-4 w-4 text-white" />
                </div>
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <QrCode className="h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground" />
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-sm sm:text-base truncate">{formatRetailerName(pkg.retailer)}</h3>
            <div className="flex items-center gap-2">
              <p className="text-xs sm:text-sm text-muted-foreground">{pkg.size} package</p>
              <Badge variant="outline" className="text-xs h-5">
                <Store className="h-3 w-3 mr-1" />
                {formatRetailerName(pkg.retailer)}
              </Badge>
            </div>
          </div>
        </div>
        <div className="flex-shrink-0">
          {getStatusBadge(pkg)}
        </div>
      </div>

      {/* Details Row */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs sm:text-sm mb-4 pb-4 border-b">
        {pkg.pickupSchedule && (
          <>
            <div className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
              <span>{format(new Date(pkg.pickupSchedule.pickup_date), "MMM d, yyyy")}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
              <span>{pkg.pickupSchedule.pickup_time_slot}</span>
            </div>
          </>
        )}
      </div>

      {/* Notes */}
      {pkg.notes && (
        <p className="text-xs sm:text-sm text-muted-foreground bg-muted/30 p-2 rounded mb-4 line-clamp-2">{pkg.notes}</p>
      )}

      {/* Action Row */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {pkg.approval_status === "pending" && (
          <>
            <Button
              size="sm"
              onClick={() => handleApprovalAction(pkg.id, "approve")}
              className="h-8 text-xs sm:text-sm"
            >
              <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleApprovalAction(pkg.id, "deny")}
              className="h-8 text-xs sm:text-sm text-destructive hover:text-destructive"
            >
              <XCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
              Deny
            </Button>
          </>
        )}
        {/* Crop QR button - available when QR code exists */}
        {pkg.qr_code_url && (
          <Button
            size="sm"
            variant={pkg.cropped_qr_url ? "outline" : "secondary"}
            onClick={() => {
              // Calculate sequence number for this package
              const allPkgs = assignedUsers.flatMap(u => u.packages);
              const sameDateRetailer = allPkgs.filter(p => {
                const pSchedule = p.pickupSchedule;
                const thisSchedule = pkg.pickupSchedule;
                return pSchedule && thisSchedule && 
                  pSchedule.pickup_date === thisSchedule.pickup_date;
              });
              const idx = sameDateRetailer.findIndex(p => p.id === pkg.id);
              const seqNum = idx >= 0 ? idx + 1 : 1;
              
              setCropPackage({
                id: pkg.id,
                qrCodeUrl: pkg.qr_code_url,
                retailer: pkg.retailer,
                pickupDate: pkg.pickupSchedule?.pickup_date || userInfo.pickupDate || null,
                sequenceNumber: seqNum,
              });
              setCropDialogOpen(true);
            }}
            className="h-8 text-xs sm:text-sm"
          >
            <Crop className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
            {pkg.cropped_qr_url ? "Re-crop QR" : "Crop QR"}
          </Button>
        )}
        {/* Inline cropped label thumbnail + print */}
        {pkg.cropped_qr_url && (
          <div className="flex items-center gap-2">
            <div
              className="w-14 h-14 sm:w-16 sm:h-16 rounded-md border overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all flex-shrink-0"
              onClick={() => handleImagePreview("qr-labels", pkg.cropped_qr_url!, "QR Label")}
              title="Click to enlarge"
            >
              <StorageImage
                bucket="qr-labels"
                storedValue={pkg.cropped_qr_url}
                alt="QR Label"
                className="w-full h-full object-contain"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                try {
                  const { data, error } = await supabase.storage
                    .from("qr-labels")
                    .createSignedUrl(pkg.cropped_qr_url!, 300);
                  if (error) throw error;
                  const printWindow = window.open("", "_blank", "width=420,height=560");
                  if (!printWindow) { toast.error("Pop-up blocked. Please allow pop-ups."); return; }
                  printWindow.document.write(`<!DOCTYPE html><html><head><title>Print Label</title><style>@page{size:2in 3in;margin:0}*{margin:0;padding:0;box-sizing:border-box}body{display:flex;align-items:center;justify-content:center;width:100vw;height:100vh;background:#fff}img{max-width:100%;max-height:100%;object-fit:contain}</style></head><body><img src="${data.signedUrl}" onload="window.print();"/></body></html>`);
                  printWindow.document.close();
                } catch (err: any) {
                  console.error("Print failed:", err);
                  toast.error("Failed to load label for printing");
                }
              }}
              className="h-8 text-xs sm:text-sm"
            >
              <Printer className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
              Print
            </Button>
          </div>
        )}
        {pkg.proof_of_pickup_url && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleImagePreview("pickup-proof", pkg.proof_of_pickup_url!, "Pickup Proof")}
            className="h-8 text-xs sm:text-sm"
          >
            <Camera className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
            View Pickup Proof
          </Button>
        )}
        {!hideEditSchedule && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleEditPackage(pkg)}
            className="h-8 text-xs sm:text-sm"
          >
            <Edit2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
            Edit Schedule
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleViewPackageDetails(pkg, userInfo)}
          className="h-8 text-xs sm:text-sm"
        >
          <Eye className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
          View Details
        </Button>

        {/* Undo lifecycle buttons */}
        {pkg.approval_status === "approved" && pkg.status !== "picked_up" && pkg.status !== "delivered" && pkg.status !== "returned" && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => openUndoDialog(pkg.id, {
              label: "Approval",
              targetApprovalStatus: "pending",
            })}
            className="h-8 text-xs sm:text-sm text-amber-600 hover:text-amber-700 hover:bg-amber-50"
          >
            <Undo2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
            Undo Approval
          </Button>
        )}
        {showMarkReturned && (
          <Button
            size="sm"
            className="h-8 text-xs sm:text-sm"
            onClick={() => handleOpenReturnDialog(pkg)}
          >
            <RotateCcw className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
            Marked Returned
          </Button>
        )}
        {pkg.status === "picked_up" && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => openUndoDialog(pkg.id, {
              label: "Picked Up",
              targetStatus: "pending",
              targetApprovalStatus: "approved",
              resetPickup: true,
              pickupId: userInfo.pickupId,
            })}
            className="h-8 text-xs sm:text-sm text-amber-600 hover:text-amber-700 hover:bg-amber-50"
          >
            <Undo2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
            Undo Picked Up
          </Button>
        )}
        {pkg.status === "returned" && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => openUndoDialog(pkg.id, {
              label: "Return",
              targetStatus: "picked_up",
            })}
            className="h-8 text-xs sm:text-sm text-amber-600 hover:text-amber-700 hover:bg-amber-50"
          >
            <Undo2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
            Undo Return
          </Button>
        )}
      </div>

      {/* Return details inline on the card */}
      {pkg.status === "returned" && (pkg.returned_at || pkg.confirmation_code || pkg.return_receipt_url) && (
        <div className="flex flex-wrap items-center gap-2 pt-3 mt-3 border-t text-xs text-muted-foreground">
          {pkg.returned_at && (
            <span className="flex items-center gap-1">
              <RotateCcw className="h-3 w-3" />
              Returned {format(new Date(pkg.returned_at), "MMM d, yyyy 'at' h:mm a")}
            </span>
          )}
          {pkg.confirmation_code && (
            <Badge variant="outline" className="text-xs h-5">
              Code: {pkg.confirmation_code}
            </Badge>
          )}
          {pkg.return_receipt_url && (
            <button
              onClick={() => handleImagePreview("return-receipts", pkg.return_receipt_url!, "Return Receipt")}
              className="text-primary hover:underline flex items-center gap-1"
            >
              <Camera className="h-3 w-3" />
              Receipt
            </button>
          )}
        </div>
      )}
    </div>
  );

  const UserTaskRow = ({ userInfo, showPickupActions = false, hideEditSchedule = false, showMarkReturned = false, onUndoPickup }: { userInfo: AssignedUser; showPickupActions?: boolean; hideEditSchedule?: boolean; showMarkReturned?: boolean; onUndoPickup?: () => void }) => {
    const isExpanded = expandedUsers.has(userInfo.id);
    const pendingCount = userInfo.packages.filter(p => p.approval_status === "pending").length;
    const totalPackages = userInfo.packages.length;

    // Group packages by drop-off location (retailer)
    const dropoffGroups = useMemo(() => {
      const groups: Record<string, number> = {};
      userInfo.packages.forEach(p => {
        const loc = formatRetailerName(p.retailer);
        groups[loc] = (groups[loc] || 0) + 1;
      });
      return groups;
    }, [userInfo.packages]);

    return (
      <Collapsible
        open={isExpanded}
        onOpenChange={() => toggleUserExpand(userInfo.id)}
      >
        {/* User header row */}
        <div className="border-b">
          <CollapsibleTrigger asChild>
            <div className="flex items-center gap-3 px-4 py-4 hover:bg-muted/30 transition-colors cursor-pointer">
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              )}

              <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <User className="h-4 w-4 text-primary" />
              </div>

              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm truncate">{userInfo.fullName}</p>
                <p className="text-xs text-muted-foreground truncate">{userInfo.email}</p>
              </div>

              {/* Pickup Address */}
              {(userInfo.pickupAddress || userInfo.address) && (
                <div className="hidden lg:flex items-center gap-1 text-xs text-muted-foreground shrink-0 max-w-[200px]">
                  <MapPin className="h-3 w-3 shrink-0" />
                  <span className="truncate">{userInfo.pickupAddress || userInfo.address}</span>
                </div>
              )}

              {/* Pickup Date & Time */}
              {userInfo.pickupDate ? (
                <div className="hidden md:flex items-center gap-2 shrink-0">
                  <div className="flex items-center gap-1 text-xs">
                    <Calendar className="h-3 w-3 text-muted-foreground" />
                    <span className="font-medium">{format(new Date(userInfo.pickupDate + "T00:00:00"), "MMM d, yyyy")}</span>
                  </div>
                  {userInfo.pickupTime && (
                    <div className="flex items-center gap-1 text-xs">
                      <Clock className="h-3 w-3 text-muted-foreground" />
                      <span>{userInfo.pickupTime}</span>
                    </div>
                  )}
                </div>
              ) : (
                <span className="hidden md:inline text-xs text-muted-foreground">No pickup scheduled</span>
              )}

              {/* Assigned Owner (admin only) */}
              {isAdmin && (
                <Badge variant="outline" className="shrink-0 text-xs">
                  {userInfo.assignedOwnerName || "Unassigned"}
                </Badge>
              )}

              {/* Pending package indicator */}
              {pendingCount > 0 && (
                <Badge className="bg-amber-100 text-amber-800 border-amber-200 shrink-0">
                  {pendingCount} pending
                </Badge>
              )}

              <Badge variant="secondary" className="shrink-0">
                <Package className="h-3 w-3 mr-1" />
                {totalPackages} package{totalPackages !== 1 ? "s" : ""}
              </Badge>

              {onUndoPickup && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50 shrink-0"
                  onClick={(e) => { e.stopPropagation(); onUndoPickup(); }}
                >
                  <Undo2 className="h-3 w-3 mr-1" />
                  Undo Pickup
                </Button>
              )}
            </div>
          </CollapsibleTrigger>

          {/* Pickup approval actions (shown outside collapsible trigger) */}
          {showPickupActions && userInfo.pickupStatus && !["completed"].includes(userInfo.pickupStatus) && (
            <div className="flex items-center gap-2 px-4 pb-3 pl-16 flex-wrap">
              <span className="text-xs text-muted-foreground mr-1">Pickup:</span>
              {userInfo.pickupDate && !["confirmed"].includes(userInfo.pickupStatus!) && (
                <>
                  <Button
                    size="sm"
                    className="h-7 text-xs"
                    onClick={(e) => { e.stopPropagation(); handlePickupApprovalAction(userInfo.id, "approve"); }}
                  >
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Approve Pickup
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs text-destructive hover:text-destructive"
                    onClick={(e) => { e.stopPropagation(); handlePickupApprovalAction(userInfo.id, "deny"); }}
                  >
                    <XCircle className="h-3 w-3 mr-1" />
                    Deny Pickup
                  </Button>
                </>
              )}
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  setSuggestTimeUserId(userInfo.id);
                  setSuggestTimeDialogOpen(true);
                }}
              >
                <Calendar className="h-3 w-3 mr-1" />
                Suggest New Time
              </Button>
            </div>
          )}
        </div>

        {/* Expanded package list */}
        <CollapsibleContent>
          <div className="bg-muted/20 p-4 space-y-3 border-b">
            {/* Drop-off location summary */}
            <div className="flex flex-wrap gap-2 mb-2">
              {Object.entries(dropoffGroups).map(([loc, count]) => (
                <Badge key={loc} variant="outline" className="text-xs">
                  <Store className="h-3 w-3 mr-1" />
                  {loc}: {count}
                </Badge>
              ))}
            </div>
            {userInfo.packages.map(pkg => (
              <PackageCard key={pkg.id} pkg={pkg} userInfo={userInfo} hideEditSchedule={hideEditSchedule} showMarkReturned={showMarkReturned} />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Loading your tasks...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <AdminContentHeader
        title="My Tasks"
        subtitle={`${filteredUsers.length} user${filteredUsers.length !== 1 ? "s" : ""} assigned to you`}
        banner={
          assignedUsers.some(u => u.packages.some(p => p.approval_status === "pending"))
            ? {
                message: `${assignedUsers.reduce((acc, u) => acc + u.packages.filter(p => p.approval_status === "pending").length, 0)} packages pending your review`,
                variant: "warning",
              }
            : undefined
        }
      />

      {/* Admin-only filter bar */}
      {isAdmin && (
        <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger className="w-[220px] bg-background">
              <SelectValue placeholder="Filter by owner" />
            </SelectTrigger>
            <SelectContent className="bg-popover">
              <SelectItem value="all">All Owners</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {logisticsManagers.map(m => (
                <SelectItem key={m.id} value={m.id}>
                  {m.name} ({m.role === "admin" ? "Admin" : "Manager"})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="h-auto flex-wrap w-full grid grid-cols-4 gap-1 p-1">
          <TabsTrigger value="pending" className="gap-1 px-2 py-2 text-xs sm:text-sm sm:px-3">
            <ClipboardCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span>Pending</span>
            {(() => {
              const count = filteredUsers.filter(u =>
                u.packages.some(p => p.approval_status === "pending")
              ).length;
              return count > 0 ? <Badge variant="secondary" className="h-5 px-1.5 text-xs">{count}</Badge> : null;
            })()}
          </TabsTrigger>
          <TabsTrigger value="approved" className="gap-1 px-2 py-2 text-xs sm:text-sm sm:px-3">
            <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span>Scheduled</span>
            {(() => {
              const count = filteredUsers.filter(u => u.pickupStatus === "confirmed").length;
              return count > 0 ? <Badge variant="secondary" className="h-5 px-1.5 text-xs">{count}</Badge> : null;
            })()}
          </TabsTrigger>
          <TabsTrigger value="picked_up" className="gap-1 px-2 py-2 text-xs sm:text-sm sm:px-3">
            <Truck className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span>Picked Up</span>
            {(() => {
              const count = filteredUsers.filter(u =>
                u.pickupStatus === "completed" &&
                u.packages.some(p => p.approval_status === "approved" && p.status !== "returned" && p.status !== "archived" && p.status !== "user_canceled")
              ).length;
              return count > 0 ? <Badge variant="secondary" className="h-5 px-1.5 text-xs">{count}</Badge> : null;
            })()}
          </TabsTrigger>
          <TabsTrigger value="returned" className="gap-1 px-2 py-2 text-xs sm:text-sm sm:px-3">
            <RotateCcw className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span>Returned</span>
          </TabsTrigger>
        </TabsList>

        {/* SECTION 1 — PENDING TASKS (Oversight Layer) */}
        <TabsContent value="pending" className="mt-4">
          {(() => {
            // Show users that have pending-review packages
            // Exclude users whose pickup is already confirmed (they belong in Section 2)
            const pendingPackageFilter = (p: PackageData) => p.approval_status === "pending";
            
            let pendingUsers = filteredUsers
              .filter(u => u.packages.some(pendingPackageFilter))
              .map(u => ({
                ...u,
                packages: u.packages.filter(pendingPackageFilter),
              }));

            // Sort by pickup date asc; users without date get "Schedule Required" badge and go to top
            pendingUsers = [...pendingUsers].sort((a, b) => {
              const aHasDate = !!a.pickupDate;
              const bHasDate = !!b.pickupDate;
              if (!aHasDate && bHasDate) return -1;
              if (aHasDate && !bHasDate) return 1;
              if (!aHasDate && !bHasDate) return 0;
              return (a.pickupDate || "").localeCompare(b.pickupDate || "");
            });

            return (
              <div className="space-y-3">
                {pendingUsers.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground border rounded-lg">
                    <ClipboardCheck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No packages pending review.</p>
                  </div>
                ) : (
                  <div className="rounded-lg border bg-card overflow-hidden shadow-sm">
                    <div className="flex items-center gap-3 px-4 py-3 bg-muted/50 border-b">
                      <span className="text-sm font-medium text-muted-foreground">
                        {pendingUsers.length} user{pendingUsers.length !== 1 ? "s" : ""} · {pendingUsers.reduce((acc, u) => acc + u.packages.length, 0)} packages
                      </span>
                    </div>
                    {pendingUsers.map(userInfo => (
                      <div key={userInfo.id}>
                        {/* Show "Schedule Required" badge for users with no pickup date */}
                        {!userInfo.pickupDate && (
                          <div className="px-4 pt-2 pl-16">
                            <Badge className="bg-amber-100 text-amber-800 border-amber-200">Schedule Required</Badge>
                          </div>
                        )}
                        <UserTaskRow userInfo={userInfo} showPickupActions hideEditSchedule />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </TabsContent>

        {/* SECTION 2 — SCHEDULED PICKUPS (Readiness Layer) */}
        <TabsContent value="approved" className="mt-4">
          {(() => {
            // Show users where pickup_status = confirmed (approved)
            // Only show approved packages (not pending ones)
            const approvedPackageFilter = (p: PackageData) => p.approval_status === "approved";

            let scheduledUsers = filteredUsers
              .filter(u => u.pickupStatus === "confirmed" && u.packages.some(approvedPackageFilter))
              .map(u => ({
                ...u,
                packages: u.packages.filter(approvedPackageFilter),
              }));

            // Sort by pickup date ascending
            scheduledUsers = [...scheduledUsers].sort((a, b) =>
              (a.pickupDate || "").localeCompare(b.pickupDate || "")
            );

            return (
              <div className="space-y-3">
                {scheduledUsers.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground border rounded-lg">
                    <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No users with scheduled pickups.</p>
                  </div>
                ) : (
                  <div className="rounded-lg border bg-card overflow-hidden shadow-sm">
                    <div className="flex items-center gap-3 px-4 py-3 bg-muted/50 border-b">
                      <span className="text-sm font-medium text-muted-foreground">
                        {scheduledUsers.length} user{scheduledUsers.length !== 1 ? "s" : ""} · {scheduledUsers.reduce((acc, u) => acc + u.packages.length, 0)} packages
                      </span>
                    </div>
                    {scheduledUsers.map(userInfo => {
                      // Check if this user has any pending (unapproved) packages in the full dataset
                      const fullUser = assignedUsers.find(u => u.id === userInfo.id);
                      const hasPendingPackages = fullUser
                        ? fullUser.packages.some(p => p.approval_status === "pending")
                        : false;

                      return (
                        <div key={userInfo.id}>
                          {/* Mark as Picked Up button at top per user */}
                          <div className="flex items-center gap-2 px-4 py-3 pl-16 border-b bg-muted/10">
                            <Button
                              size="sm"
                              className="h-8 text-xs bg-green-600 hover:bg-green-700"
                              disabled={hasPendingPackages}
                              onClick={() => {
                                if (!userInfo.pickupId) {
                                  toast.error("No pickup found for this user");
                                  return;
                                }
                                setConfirmingUserId(userInfo.id);
                                setConfirmationPhotoFile(null);
                                setConfirmationPhotoPreview(null);
                                setPickupConfirmDialogOpen(true);
                              }}
                            >
                              <Truck className="h-3.5 w-3.5 mr-1" />
                              Mark as Picked Up
                            </Button>
                            {hasPendingPackages && (
                              <span className="text-xs text-amber-600">
                                Approve or deny all pending packages first
                              </span>
                            )}
                          </div>
                          <UserTaskRow userInfo={userInfo} hideEditSchedule />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}
        </TabsContent>

        {/* SECTION 3 — PICKED UP (Execution Complete) */}
        <TabsContent value="picked_up" className="mt-4">
          {(() => {
            // Show users where pickup_status = completed AND has at least one unreturned package
            // Completed + zero packages = fully resolved, skip
            const unreturnedFilter = (p: PackageData) =>
              p.approval_status === "approved" && p.status !== "returned" && p.status !== "archived" && p.status !== "user_canceled";

            let pickedUpUsers = filteredUsers
              .filter(u =>
                u.pickupStatus === "completed" &&
                u.packages.some(unreturnedFilter)
              )
              .map(u => ({
                ...u,
                packages: u.packages.filter(unreturnedFilter),
              }));

            // Sort by pickup completion timestamp descending (newest first)
            // We use the pickup's completed_at which maps to the user's pickupDate as proxy
            pickedUpUsers = [...pickedUpUsers].sort((a, b) => {
              // Sort newest first - we don't have completed_at directly, but completed pickups are recent
              return (b.pickupDate || "").localeCompare(a.pickupDate || "");
            });

            return (
              <div className="space-y-3">
                {pickedUpUsers.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground border rounded-lg">
                    <Truck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No pickups awaiting return confirmation.</p>
                  </div>
                ) : (
                  <div className="rounded-lg border bg-card overflow-hidden shadow-sm">
                    <div className="flex items-center gap-3 px-4 py-3 bg-muted/50 border-b">
                      <span className="text-sm font-medium text-muted-foreground">
                        {pickedUpUsers.length} user{pickedUpUsers.length !== 1 ? "s" : ""} · {pickedUpUsers.reduce((acc, u) => acc + u.packages.length, 0)} packages awaiting return
                      </span>
                    </div>
                    {pickedUpUsers.map(userInfo => (
                      <div key={userInfo.id}>
                        <UserTaskRow
                          userInfo={userInfo}
                          hideEditSchedule
                          showMarkReturned
                          onUndoPickup={() => openUndoDialog(userInfo.id, {
                            label: "Pickup Completion",
                            targetStatus: "pending",
                            targetApprovalStatus: "approved",
                            resetPickup: true,
                            pickupId: userInfo.pickupId,
                            isUserLevel: true,
                            userPackageIds: userInfo.packages.map(p => p.id),
                          })}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </TabsContent>

        {/* SECTION 4 — RETURNED (Archive) */}
        <TabsContent value="returned" className="mt-4">
          {(() => {
            // Package-level archive: show packages with status = returned, within 30 days of returned_at
            const cutoff30 = new Date();
            cutoff30.setDate(cutoff30.getDate() - 30);
            const cutoffStr = cutoff30.toISOString();

            const returnedFilter = (p: PackageData) =>
              p.status === "returned" &&
              p.returned_at &&
              p.returned_at >= cutoffStr;

            let returnedUsers = filteredUsers
              .filter(u => u.packages.some(returnedFilter))
              .map(u => ({
                ...u,
                packages: u.packages.filter(returnedFilter),
              }));

            // Sort by most recent returned_at descending
            returnedUsers = [...returnedUsers].sort((a, b) => {
              const latestA = a.packages.reduce((max, p) => {
                const d = p.returned_at || "";
                return d > max ? d : max;
              }, "");
              const latestB = b.packages.reduce((max, p) => {
                const d = p.returned_at || "";
                return d > max ? d : max;
              }, "");
              return latestB.localeCompare(latestA);
            });

            return (
              <div className="space-y-3">
                {returnedUsers.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground border rounded-lg">
                    <RotateCcw className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No returned packages.</p>
                  </div>
                ) : (
                  <div className="rounded-lg border bg-card overflow-hidden shadow-sm">
                    <div className="flex items-center gap-3 px-4 py-3 bg-muted/50 border-b">
                      <span className="text-sm font-medium text-muted-foreground">
                        {returnedUsers.length} user{returnedUsers.length !== 1 ? "s" : ""} · {returnedUsers.reduce((acc, u) => acc + u.packages.length, 0)} returned packages
                      </span>
                    </div>
                    {returnedUsers.map(userInfo => (
                      <div key={userInfo.id}>
                        <UserTaskRow userInfo={userInfo} hideEditSchedule />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </TabsContent>
      </Tabs>

      <EditPackageDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        packageData={editingPackage}
        managers={logisticsManagers}
        onSave={handleSavePackage}
        loading={editLoading}
      />

      <AlertDialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <AlertDialogContent className="bg-background">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {approvalAction === "approve" ? "Approve Package?" : "Deny Package?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {approvalAction === "approve"
                ? "This will approve the package and move it to Scheduled."
                : "Please provide a reason for denying this package. The user will be notified."}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {approvalAction === "deny" && (
            <div className="py-2">
              <textarea
                value={denialReason}
                onChange={(e) => setDenialReason(e.target.value)}
                placeholder="Enter reason for denial (required)..."
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {denialReason.length}/500 characters
              </p>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmApproval}
              disabled={approvalLoading || (approvalAction === "deny" && !denialReason.trim())}
              className={approvalAction === "deny" ? "bg-destructive hover:bg-destructive/90" : ""}
            >
              {approvalLoading ? "Processing..." : approvalAction === "approve" ? "Approve" : "Deny"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pickup Date/Time Approval Dialog */}
      <AlertDialog open={pickupApprovalDialogOpen} onOpenChange={setPickupApprovalDialogOpen}>
        <AlertDialogContent className="bg-background">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pickupApprovalAction === "approve" ? "Approve Pickup Date/Time?" : "Deny Pickup Date/Time?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pickupApprovalAction === "approve"
                ? "This will confirm the user's requested pickup date and time."
                : "Please provide a reason for denying this pickup request."}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {pickupApprovalAction === "deny" && (
            <div className="py-2">
              <textarea
                value={pickupDenialReason}
                onChange={(e) => setPickupDenialReason(e.target.value)}
                placeholder="Enter reason for denial (required)..."
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {pickupDenialReason.length}/500 characters
              </p>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmPickupApproval}
              disabled={pickupApprovalLoading || (pickupApprovalAction === "deny" && !pickupDenialReason.trim())}
              className={pickupApprovalAction === "deny" ? "bg-destructive hover:bg-destructive/90" : ""}
            >
              {pickupApprovalLoading ? "Processing..." : pickupApprovalAction === "approve" ? "Approve" : "Deny"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={imagePreviewOpen} onOpenChange={setImagePreviewOpen}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle>{previewImage?.title || "Image Preview"}</DialogTitle>
          </DialogHeader>
          <div className="p-4">
            {previewImage && (
              <StorageImage
                bucket={previewImage.bucket}
                storedValue={previewImage.path}
                alt={previewImage.title}
                className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark as Picked Up Dialog */}
      <Dialog open={pickupDialogOpen} onOpenChange={setPickupDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark as Picked Up</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Optionally upload a photo as proof of pickup. This will be visible to the customer in their tracking.
            </p>

            <div className="space-y-3">
              <label
                htmlFor="pickup-proof-upload"
                className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer bg-muted/30 hover:bg-muted/50 transition-colors"
              >
                {proofOfPickupPreview ? (
                  <img
                    src={proofOfPickupPreview}
                    alt="Proof preview"
                    className="w-full h-full object-contain rounded-lg"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="w-10 h-10 mb-3 text-muted-foreground" />
                    <p className="mb-2 text-sm text-muted-foreground">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-muted-foreground">PNG, JPG, or WEBP</p>
                  </div>
                )}
                <input
                  id="pickup-proof-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePickupProofFileChange}
                />
              </label>

              {proofOfPickupFile && (
                <p className="text-xs text-muted-foreground text-center">
                  {proofOfPickupFile.name}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setPickupDialogOpen(false)}
                disabled={pickupLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleMarkAsPickedUp}
                disabled={pickupLoading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {pickupLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {proofOfPickupFile ? "Uploading..." : "Updating..."}
                  </>
                ) : (
                  <>
                    <Truck className="h-4 w-4 mr-2" />
                    Mark as Picked Up
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ReturnCompletionDialog
        open={returnDialogOpen}
        onOpenChange={setReturnDialogOpen}
        packageId={returningPackageId}
        packageName={returningPackageName}
        onComplete={fetchAssignedUsers}
      />

      {/* Pickup Confirmation Photo Dialog (per-user) */}
      <Dialog open={pickupConfirmDialogOpen} onOpenChange={setPickupConfirmDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Pickup Confirmation Photo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Upload a photo showing the collected packages (e.g., doorstep photo). This confirms the pickup for all packages.
            </p>

            <div className="space-y-3">
              <label
                htmlFor="confirmation-photo-upload"
                className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer bg-muted/30 hover:bg-muted/50 transition-colors"
              >
                {confirmationPhotoPreview ? (
                  <img
                    src={confirmationPhotoPreview}
                    alt="Confirmation preview"
                    className="w-full h-full object-contain rounded-lg"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="w-10 h-10 mb-3 text-muted-foreground" />
                    <p className="mb-2 text-sm text-muted-foreground">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-muted-foreground">PNG, JPG, or WEBP</p>
                  </div>
                )}
                <input
                  id="confirmation-photo-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setConfirmationPhotoFile(file);
                      setConfirmationPhotoPreview(URL.createObjectURL(file));
                    }
                  }}
                />
              </label>

              {confirmationPhotoFile && (
                <p className="text-xs text-muted-foreground text-center">
                  {confirmationPhotoFile.name}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setPickupConfirmDialogOpen(false)}
                disabled={confirmationLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (!confirmingUserId || !confirmationPhotoFile) return;
                  const userInfo = assignedUsers.find(u => u.id === confirmingUserId);
                  if (!userInfo?.pickupId) return;

                  setConfirmationLoading(true);
                  try {
                    // Upload photo to pickup-proof bucket
                    const fileExt = confirmationPhotoFile.name.split('.').pop();
                    const fileName = `${userInfo.pickupId}/${Date.now()}.${fileExt}`;

                    const { error: uploadError } = await supabase.storage
                      .from('pickup-proof')
                      .upload(fileName, confirmationPhotoFile);
                    if (uploadError) throw uploadError;

                    // Update pickup with confirmation photo and mark as completed
                    const { error: updateError } = await (supabase as any)
                      .from('user_pickups')
                      .update({
                        pickup_confirmation_photo_url: fileName,
                        status: 'completed',
                        completed_at: new Date().toISOString(),
                      })
                      .eq('id', userInfo.pickupId);
                    if (updateError) throw updateError;

                    toast.success("Pickup confirmed — packages moved to Confirmed Delivered");
                    setPickupConfirmDialogOpen(false);
                    setConfirmingUserId(null);
                    setConfirmationPhotoFile(null);
                    setConfirmationPhotoPreview(null);
                    await fetchAssignedUsers();
                  } catch (error: any) {
                    console.error("Error confirming pickup:", error);
                    toast.error(error.message || "Failed to confirm pickup");
                  } finally {
                    setConfirmationLoading(false);
                  }
                }}
                disabled={confirmationLoading || !confirmationPhotoFile}
              >
                {confirmationLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Camera className="h-4 w-4 mr-2" />
                    Confirm Pickup
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Suggest New Time (Counter-Proposal) Dialog */}
      <ProposePickupDialog
        open={suggestTimeDialogOpen}
        onOpenChange={setSuggestTimeDialogOpen}
        userData={(() => {
          if (!suggestTimeUserId) return null;
          const u = assignedUsers.find(x => x.id === suggestTimeUserId);
          if (!u) return null;
          return {
            userId: u.id,
            userName: u.fullName,
            email: u.email,
            address: u.pickupAddress || u.address || "",
            packages: u.packages.map(p => ({
              id: p.id,
              retailer: p.retailer,
              size: p.size,
              approval_status: p.approval_status,
            })),
          };
        })()}
        managers={logisticsManagers}
        onPropose={async (data) => {
          setSuggestTimeLoading(true);
          try {
            const userInfo = assignedUsers.find(u => u.id === data.userId);
            if (!userInfo?.pickupId) {
              toast.error("No pickup found for this user");
              return;
            }

            const { error } = await (supabase as any)
              .from("user_pickups")
              .update({
                counter_proposed_date: format(data.proposedDate, "yyyy-MM-dd"),
                counter_proposed_time_slot: data.proposedTimeSlot,
                status: "counter_proposed",
              })
              .eq("id", userInfo.pickupId);

            if (error) throw error;

            toast.success("New time suggested — awaiting user confirmation");
            setSuggestTimeDialogOpen(false);
            setSuggestTimeUserId(null);
            await fetchAssignedUsers();
          } catch (error: any) {
            console.error("Error suggesting new time:", error);
            toast.error(error.message || "Failed to suggest new time");
          } finally {
            setSuggestTimeLoading(false);
          }
        }}
        loading={suggestTimeLoading}
      />

      <PackageDetailDrawer
        open={detailDrawerOpen}
        onOpenChange={setDetailDrawerOpen}
        packageData={detailPackageData}
        actions={
          <Button className="w-full" onClick={() => setDetailDrawerOpen(false)}>
            Close
          </Button>
        }
      />

      {/* Undo Lifecycle Dialog */}
      <AlertDialog open={undoDialogOpen} onOpenChange={setUndoDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Undo2 className="h-5 w-5 text-amber-600" />
              Reverse {undoAction?.label}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will move the package back to its previous lifecycle stage. Please provide a reason for this reversal.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4 space-y-3">
            <Label htmlFor="undo-reason">Reason (required)</Label>
            <Textarea
              id="undo-reason"
              placeholder="Explain why this action is being reversed..."
              value={undoReason}
              onChange={(e) => setUndoReason(e.target.value)}
              maxLength={500}
              className="min-h-[80px]"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={undoLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUndoConfirm}
              disabled={undoLoading || !undoReason.trim()}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {undoLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Reversing...
                </>
              ) : (
                <>
                  <Undo2 className="h-4 w-4 mr-2" />
                  Confirm Reversal
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* QR Crop Dialog */}
      <QrCropDialog
        open={cropDialogOpen}
        onOpenChange={setCropDialogOpen}
        qrCodeUrl={cropPackage?.qrCodeUrl || null}
        packageId={cropPackage?.id || ""}
        retailer={cropPackage?.retailer || ""}
        pickupDate={cropPackage?.pickupDate || null}
        sequenceNumber={cropPackage?.sequenceNumber || 1}
        onComplete={() => fetchAssignedUsers()}
      />
    </div>
  );
};
